import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { LoadingTemplate } from "../helpful";
import {SliderSkeleton} from "../skeleton";

/**
 * The Slider component displays a carousel of images and videos with play/pause functionality and
 * indicators for navigation.
 * @returns The Slider component is returning a div element containing the Carousel component, which
 * in turn renders the image or video slider based on the items passed as props. The Carousel
 * component handles the logic for displaying and transitioning between images or videos, along with
 * indicators for navigation.
 *  @author lionel ghogue ngondeke <lionelladrog@gmail.com> 2024/11/12
 */

export default function Slider({ slideImages }) {
  const [images, setImages] = useState([]);

  const { t } = useTranslation();

  useEffect(() => {
    if (slideImages) {
      setImages(slideImages);
    }
  }, [slideImages]);

  const Carousel = ({ items }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [type, setType] = useState(null);
    const [itemSrc, setItemSrc] = useState("");
    const [desc, setDesc] = useState(null);
    const [isPlaying, setPlayPause] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [duration, setDuration] = useState(5000);
    const videoRef = useRef(null);

    useEffect(() => {
      const element = items[currentIndex];

      if (element !== undefined) {
        setIsLoading(false);

        if ("itemVideo" in element && element.itemVideo.trim() !== "") {
          setType("video");
          setItemSrc(element.itemVideo);
          setDesc(element.desc);
          // setPlayPause(false)
        } else if (
          "itemImageSrc" in element &&
          element.itemImageSrc.trim() !== ""
        ) {
          setType("image");
          setPlayPause(true)
          setDesc(element.desc);
          setItemSrc(element.itemImageSrc);
          setDuration(5000); // Default duration for images
        }
      } else {
        setIsLoading(true);
      }
    }, [currentIndex, items]);

    useEffect(() => {
      if (type === "video" && videoRef.current) {
        const videoElement = videoRef.current;

        const handleMetadataLoaded = () => {
          const videoDuration = Math.floor(videoElement.duration * 1000);
          setDuration(videoDuration > 0 ? videoDuration : 5000);
        };

        videoElement.addEventListener("loadedmetadata", handleMetadataLoaded);

        return () => {
          videoElement.removeEventListener(
            "loadedmetadata",
            handleMetadataLoaded
          );
        };
      }
    }, [type, itemSrc]);

    const playPause = () => {
      var myVideo = document.getElementById("carousel-video");
      if (type === "video" && videoRef.current) {
        if (myVideo.paused){
          myVideo.play();
          setPlayPause(true)
        } 
        else{
          myVideo.pause();
          setPlayPause(false)
        } 
      }
      
    };

  

    useEffect(() => {
      const intervalId = setInterval(() => {
        nextSlide();
      }, duration);
      return () => clearInterval(intervalId);
    }, [duration, currentIndex]);

    const nextSlide = () => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
    };

    const prevSlide = () => {
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + items.length) % items.length
      );
    };

   
    const handleIndicatorClick = (index) => {
      setCurrentIndex(index);
    };

    const Indicator = () => {
      if (items.length > 0) {
        return (
          <div className="d-flex">
            {items.map((item, index) => {
              return (
                <div
                  key={index}
                  className={`indicator-btn ${
                    index === currentIndex ? "indicator-active" : ""
                  }`}
                  onClick={() => handleIndicatorClick(index)}
                ></div>
              );
            })}
          </div>
        );
      }
    };

    if (isLoading) {
      return  <LoadingTemplate />
    }


    return (
      <div className="carousel-wrapper">
        
        <div className={`carousel-wrapper-border ${!isPlaying?'carousel-wrapper-border-bg': ''}`} onClick={playPause}>
          {!isPlaying?<i className="bi bi-play-circle carousel-play-btn"></i>:null}
         
        </div>

        <motion.div
          key={type === "video" ? "video" : "image"}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="carousel-slider">
            {type === "video" ? (
              <div className="">
                <video
                  id="carousel-video"
                  ref={videoRef}
                  className="carousel-video"
                  width="100%"
                  height="auto"
                  // controls
                  loop={true}
                  autoPlay
                  muted
                  onClick={playPause}
                >
                  <source src={itemSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <div className="">
                <img
                  className="carousel-image"
                  src={itemSrc}
                  alt={"Carousel Item"}
                  width="100%"
                  height="auto"
                />
              </div>
            )}
          </div>
        </motion.div>
        <div className="d-flex justify-content-between mt-3">
          <div>
            <h6 className="carousel-description ">{desc}</h6>
          </div>
          <div>
            {items.length <= 1?null: <Indicator />}
           
          </div>
        </div>
      
      </div>
    );
  };
  
  return (
    
    <div>
      {images.length>0? <Carousel items={images} />:<SliderSkeleton />}
      
    </div>
  );
}
